import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { createLoginFormGroup } from '../../shared/utils/login-form.util';
import { LoginLayoutComponent } from '../../components/login-layout/login-layout.component';
import { ErrorMessageComponent } from '../../components/error-message/error-message.component';
import { RoleService } from '../../services/role.service';
import { ApiService } from '../../services/api.service';
import { UserRole } from '../../shared/types/role.interface';

/** Super Admin login at `/admin/login` (env bootstrap or DB `adminpanel_users`). */
@Component({
  selector: 'app-admin-login',
  standalone: true,
  imports: [
    CommonModule,
    LoginLayoutComponent,
    FormsModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
  ],
  templateUrl: './admin-login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-login.component.scss'],
})
export class AdminLoginComponent implements OnInit {
  isSubmitting = false;
  emailError = '';
  passwordError = '';
  showPassword = false;

  loginForm = createLoginFormGroup();

  constructor(
    private router: Router,
    private apiService: ApiService,
    private roleService: RoleService
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const superAdminData = localStorage.getItem('superAdminData');
    if (superAdminData) {
      try {
        const parsedData = JSON.parse(superAdminData);
        if (parsedData?.token) {
          this.roleService.setUserRole(UserRole.SUPER_ADMIN);
          if (this.roleService.getCurrentUserRole() === UserRole.SUPER_ADMIN) {
            void this.router.navigate(['/admin/dashboard']);
          }
        }
      } catch {
        localStorage.removeItem('superAdminData');
      }
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  login() {
    if (this.loginForm.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.emailError = '';
    this.passwordError = '';

    const payload = {
      username: this.loginForm.value.email || '',
      password: this.loginForm.value.password || '',
    };

    this.apiService.postWithoutAuth('admin/super-admin/login', payload).subscribe({
      next: response => {
        if (response.status === true && response.data) {
          this.handleLoginSuccess(response.data, payload);
          return;
        }
        this.isSubmitting = false;
        this.passwordError = response.message || 'Login failed. Please try again.';
      },
      error: err => {
        this.isSubmitting = false;
        this.passwordError =
          err?.error?.message || err?.message || 'Invalid credentials. Please try again.';
      },
    });
  }

  private handleLoginSuccess(
    data: Record<string, any>,
    payload: { username: string; password: string }
  ): void {
    const role = String(data['role'] || '').toLowerCase();
    if (role !== UserRole.SUPER_ADMIN && role !== 'super_admin') {
      this.isSubmitting = false;
      this.passwordError = 'Only super admin accounts can sign in here.';
      return;
    }

    const superAdminData = {
      token: data['token'] || data['access_token'],
      username: data['username'] || payload.username,
      email: data['email'] || payload.username,
      role: UserRole.SUPER_ADMIN,
      rememberMe: this.loginForm.value.rememberMe || false,
      date: new Date(),
      ...data,
    };

    localStorage.setItem('superAdminData', JSON.stringify(superAdminData));
    this.roleService.setUserRole(UserRole.SUPER_ADMIN);
    this.isSubmitting = false;
    void this.router.navigate(['/admin/dashboard']);
  }
}
